import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';

import { useCheckout, useUserPreferences } from '@jane/shared-ecomm/providers';
import type {
  CheckoutNewPaymentMethods,
  CheckoutNewRequest,
} from '@jane/shared-ecomm/types';
import type { Address } from '@jane/shared/types';
import { formatTime, getAnonymousUserId } from '@jane/shared/util';

import type { CanPayIntent } from '../../CheckoutPayments/CheckoutPaymentsProvider';
import { useCheckoutPayments } from '../../CheckoutPayments/CheckoutPaymentsProvider';
import { useHeadlessKiosk } from '../../HeadlessKioskProvider';
import { useCheckoutDocs } from './useCheckoutDocs';
import { PaymentMethods } from './usePaymentOptions';
import { useUserConsents } from './useUserConsents';

export const useBuildCheckoutRequest = () => {
  const location = useLocation();
  const { userLocation, userPreferences } = useUserPreferences();
  const { isHeadlessKioskMode } = useHeadlessKiosk();

  const { checkoutConsents } = useUserConsents();
  const {
    requiredDocs: {
      governmentId,
      medicalExpirationDate,
      medicalPhoto,
      medicalNumber,
    },
  } = useCheckoutDocs();

  const { getValues } = useFormContext();

  const {
    appInfo: { appMode, headlessCheckoutPartnerId, janeDeviceId },
    cart,
    customer,
    deliveryAddress,
    checkoutRequirements,
    googleValidatedAddress,
    store,
    tags,
    userGroupSpecialId,
  } = useCheckout();
  const {
    aeropayBankId,
    canPayIntent,
    monerisTicket,
    selected,
    squareToken,
    strongholdSourceId,
  } = useCheckoutPayments();

  const buildPaymentKeys = useCallback((): CheckoutNewPaymentMethods => {
    const { payfirma, canPayToken } = getValues('paymentOptions');

    switch (selected) {
      case PaymentMethods.janePay:
        return {
          payment_method: PaymentMethods.janePay,
          aeropay_bank_account_id: aeropayBankId,
        };
      case PaymentMethods.payfirma:
        return {
          payment_method: PaymentMethods.payfirma,
          payfirma_card_cvv2: payfirma.cardCvv2,
          payfirma_card_expiry_month: payfirma.cardExpiry.slice(0, 2),
          payfirma_card_expiry_year: payfirma.cardExpiry.slice(2),
          payfirma_card_number: payfirma.cardNumber,
          payfirma_card_postal_code: payfirma.cardPostalCode,
        };
      case PaymentMethods.canpay:
        return {
          payment_method: PaymentMethods.canpay,
          canpay_token: canPayToken,
          canpay_v2remotepay_transaction: canPayIntent as CanPayIntent,
        };
      case PaymentMethods.moneris:
        return {
          payment_method: PaymentMethods.moneris,
          moneris_ticket: monerisTicket,
        };
      case PaymentMethods.square:
        return {
          payment_method: PaymentMethods.square,
          square_payment_token: squareToken,
        };
      case PaymentMethods.stronghold:
        return {
          payment_method: PaymentMethods.stronghold,
          stronghold_payment_source_id: strongholdSourceId,
        };
      default:
        return {
          payment_method: selected as string,
        };
    }
  }, [
    aeropayBankId,
    canPayIntent,
    getValues,
    monerisTicket,
    selected,
    squareToken,
    strongholdSourceId,
  ]);

  const buildRequestValues = useCallback((): CheckoutNewRequest => {
    const [customerInformation, pickupOptions, deliveryOptions] = getValues([
      'customerInformation',
      'pickupOptions',
      'deliveryOptions',
    ]);

    const pickupMode = pickupOptions.pickupMode;

    const isGuestCheckout = location.pathname.includes('guest_checkout');

    const fulfillmentOptions =
      cart.reservation_mode === 'delivery' ? deliveryOptions : pickupOptions;

    const isCurbside =
      cart.reservation_mode === 'pickup' && pickupMode === 'curbside';

    const [delivery_started_at, delivery_finished_at] =
      fulfillmentOptions.fulfillmentTime?.split('>') || [];

    const formattedBirthDate = customerInformation.birthDate
      ? formatTime({
          time: customerInformation.birthDate,
          outputFormat: 'YYYY-MM-DD',
          inputFormat: 'MMDDYYYY',
        })
      : '';

    const buildPickupAddress = (): { customer_pickup_address?: Address } => {
      if (!store.customer_pickup_address_required || !googleValidatedAddress) {
        return {};
      }

      const customer_pickup_address: Address = {
        street: googleValidatedAddress?.street,
        ...(customerInformation.street2 && {
          street2: customerInformation.street2,
        }),
        city: googleValidatedAddress?.city,
        state_code: googleValidatedAddress?.state,
        zipcode: googleValidatedAddress?.zipcode,
        country_code: googleValidatedAddress?.countryCode,
        lat: googleValidatedAddress?.coordinates?.lat,
        lng: googleValidatedAddress?.coordinates?.long,
      };

      return { customer_pickup_address };
    };

    const requestValues = {
      age_verification: {
        birth_date: formattedBirthDate,
        government_identification_url: governmentId
          ? customerInformation.governmentIdentificationUrl
          : null,
      },
      ...(isGuestCheckout && { anonymous_user_id: getAnonymousUserId() }),
      ...(isHeadlessKioskMode && { headless_kiosk: true }),
      current_url: window.location.href,
      customer: {
        email: customerInformation.email,
        external_user_id: customer.externalId,
        first_name: customerInformation.firstName,
        last_name: customerInformation.lastName,
        phone: customerInformation.phone,
      },
      ...(cart.reservation_mode === 'delivery' && {
        delivery_address: deliveryAddress ?? undefined,
        delivery_fee_amount: cart.delivery_fee_amount,
      }),
      delivery_finished_at: parseInt(delivery_finished_at),
      delivery_started_at: parseInt(delivery_started_at),
      headless_checkout_partner_id: headlessCheckoutPartnerId,
      is_curbside_pickup: isCurbside,
      j_device_id: janeDeviceId,
      medical_identification: {
        id_back_url: medicalPhoto ? customerInformation.mmjIdBackUrl : null,
        id_front_url: medicalPhoto ? customerInformation.mmjIdFrontUrl : null,
        id_number: medicalNumber ? customerInformation.mmjIdNumber : null,
        expiration_date: medicalExpirationDate
          ? formatTime({
              time: customerInformation.mmjExpirationDate,
              outputFormat: 'YYYY-MM-DD',
              inputFormat: 'MMDDYYYY',
            })
          : null,
      },
      message: fulfillmentOptions.message || '',
      promotions_opt_out:
        appMode === 'default' ? !fulfillmentOptions.promotionsOptIn : undefined,
      reservation_mode: cart.reservation_mode,
      tags: tags || {},
      tip_amount: cart.tip_amount || 0,
      uuid: cart.uuid,
      user_consent: checkoutConsents || [],
      user_group_special_id: userGroupSpecialId,
      user_location: userLocation,
      user_preferences: userPreferences,
      ...buildPickupAddress(),
      ...buildPaymentKeys(),
    };

    return requestValues;
  }, [
    buildPaymentKeys,
    cart.reservation_mode,
    cart.tip_amount,
    cart.uuid,
    checkoutConsents,
    checkoutRequirements.data?.medical_documentation_requirement,
    customer.externalId,
    deliveryAddress,
    isHeadlessKioskMode,
    location.pathname,
    store.customer_pickup_address_required,
    tags,
    userGroupSpecialId,
    userLocation,
    userPreferences,
  ]);

  return { buildRequestValues };
};
